import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import type { GarmentCard, SessionCommand } from '@fitting-room/contracts'
import { checkIntakeValue, isCalendarDay } from '@fitting-room/contracts/intake'
import { StepShell } from '../../components/RoomChrome'
import { GarmentCardFace } from '../../components/GarmentCardFace'
import { NOTE_SCOPE, draftStore, useDraftFlush } from '../../lib/drafts'
import { formatDateTime, formatDay } from '../../lib/format'
import { uiStrings } from '../../lib/strings'
import { text } from '../../lib/text'
import { contactFields, isUnchanged, openingValue, type ContactField } from '../contactFields'
import { useGarmentsByIds, useRefill } from '../useSession'
import type { StepProps } from '../types'

export function SummaryStep({ step, ctx }: StepProps<'summary'>) {
  const { view, locale, token } = ctx
  const t = uiStrings(locale)
  const submitted = view.session.status === 'submitted'
  /**
   * Nobody booked this one: she came in through the public door.
   *
   * Everything this flag turns on is additional, and only for her. A session
   * that hangs off an appointment reaches this screen exactly as it did
   * before — same rail, same one button, nothing new to read or agree to.
   */
  const guest = view.session.visitId === undefined

  const ids = useMemo(() => view.session.picks.map((p) => p.garmentId), [view.session.picks])
  const drafts = useMemo(() => draftStore(token), [token])

  /**
   * A note she was part-way through when the tab went away reopens itself.
   *
   * A draft she cannot see is one she has to take on trust, and the button
   * beneath the card would show her the SAVED note while the unsent one sat
   * invisibly behind it. Only a pick still in the rail can reopen — she may
   * have taken the garment off it since. Lazy, like the intake screen, so a
   * refetch never reopens an editor she has closed.
   */
  const [editing, setEditing] = useState<string | null>(() => {
    const pending = drafts.readScope(NOTE_SCOPE)
    return ids.find((id) => pending[id] !== undefined) ?? null
  })
  const [draft, setDraft] = useState(() => (editing === null ? '' : (drafts.read(NOTE_SCOPE, editing) ?? '')))

  /**
   * The one note open in the editor, and nothing once the session is
   * submitted: that empties the entries, which cancels the pending timer
   * before `SessionRoute` wipes the session's drafts — a write landing after
   * that would leave her note on the phone after the appointment.
   */
  const noteDrafts = useMemo<Record<string, string>>(
    () => (editing === null || submitted ? {} : { [editing]: draft }),
    [editing, draft, submitted],
  )
  useDraftFlush(drafts, NOTE_SCOPE, noteDrafts)

  const garments = useGarmentsByIds(token, ids)
  const byId = useMemo(() => {
    const map = new Map<string, GarmentCard>()
    for (const item of garments.data?.items ?? []) map.set(item.garment.id, item)
    return map
  }, [garments.data])

  /**
   * Where a name and an address given on this screen go: the flow's own
   * contact fields, found by what the flow says about them rather than by
   * what a boutique happened to call them. One finder, two callers — the
   * panel below and the guest's last step — because two answers to "which
   * field is the address" is one answer too many. See `../contactFields`.
   */
  const contact = useMemo(() => contactFields(view.flow.steps), [view.flow.steps])
  const address = contact.address

  const stored = address ? view.session.profile[address.field.key] : undefined
  /** What the session already holds, then the booking — as the intake screen opens. */
  const opening = openingValue(address, view.session.profile, view.profilePrefill)

  const [panelOpen, setPanelOpen] = useState(false)
  /*
   * Kept in component state and NOWHERE else.
   *
   * The session's drafts are wiped the moment its status turns submitted, and
   * that has already happened by the time this panel can be on screen — so a
   * draft written from here would never be cleared again, and an address would
   * outlive the appointment on a phone that may have been borrowed. Losing a
   * half-typed address to a reload is the cheaper of the two.
   *
   * The guest's fields below share that rule for the other half of the reason:
   * the send that carries them is the same tap that submits, so a draft
   * written here would be racing the wipe that the submit sets off.
   */
  const [typed, setTyped] = useState(opening)
  /** The address she asked us to keep, so "saved" is the server agreeing rather than a hope. */
  const [asked, setAsked] = useState<string | null>(null)
  const [shared, setShared] = useState(false)
  const [railOpen, setRailOpen] = useState(false)

  /*
   * Filling it in again, which is a different session and a different link.
   *
   * Offered only while there is still time to use it: past the appointment the
   * server refuses the refill on the same deadline that closes this session,
   * and a control that can only fail is worse than one that is not there. The
   * server is the rule — this is the affordance.
   */
  const navigate = useNavigate()
  const refill = useRefill(token)
  const deadlinePassed = view.session.editableUntil !== undefined && Date.now() > Date.parse(view.session.editableUntil)

  /** Her name, opened the same way, for the screen that asks before sending. */
  const [typedName, setTypedName] = useState(() => openingValue(contact.name, view.session.profile, view.profilePrefill))
  /** Whether the send has asked its questions yet. Guests only; see `asksFirst`. */
  const [asking, setAsking] = useState(false)
  /*
   * The claim opens from the session, so a link opened tomorrow shows the box
   * ticked and her date in it — which is what makes UNticking it possible at
   * all, rather than only on the tab that ticked it.
   */
  const claim = view.session.bookingClaim
  const [claiming, setClaiming] = useState(() => claim !== undefined)
  const [claimDay, setClaimDay] = useState(() => claim?.claimedDate ?? '')

  /**
   * The same check the server runs, from the same function in the contract —
   * a client gating on a different rule is a form that refuses what it just
   * accepted, with nothing to show for it but "that did not save".
   */
  const refusalFor = (target: ContactField | null, raw: string): string | null => {
    if (!target) return null
    const trimmed = raw.trim()
    if (trimmed === '') return null
    const checked = checkIntakeValue(target.field, trimmed)
    return checked.ok ? null : t.intakeProblem(checked, target.field.unit)
  }
  const refusal = refusalFor(address, typed)
  const nameRefusal = refusalFor(contact.name, typedName)
  /*
   * The claimed day is not an intake field — no field asked for it — so it
   * cannot go through `checkIntakeValue`. It goes through the same rule that
   * function uses for a date, exported from the contract for this one caller,
   * and is refused in the same words.
   */
  const dayRefusal =
    claiming && claimDay.trim() !== '' && !isCalendarDay(claimDay.trim())
      ? t.intakeProblem({ ok: false, reason: 'not_a_date' })
      : null

  const saved = asked !== null && stored !== undefined && String(stored) === asked

  const keepAddress = () => {
    if (!address) return
    const raw = typed.trim()
    ctx.send([{ type: 'profile', stepId: address.stepId, values: { [address.field.key]: raw } }])
    setAsked(raw)
  }

  /**
   * What she typed, addressed to the steps that own the fields.
   *
   * `profile` is keyed by step, and a name and an address need not be on the
   * same one, so the writes are grouped rather than assumed to be a single
   * command. Nothing is sent for a value the session already holds exactly:
   * the point of this screen is the values she has changed.
   */
  const profileWrites = (): SessionCommand[] => {
    const byStep = new Map<string, Record<string, string>>()
    const put = (target: ContactField | null, raw: string) => {
      const trimmed = raw.trim()
      if (!target || trimmed === '' || isUnchanged(target, trimmed, view.session.profile)) return
      byStep.set(target.stepId, { ...byStep.get(target.stepId), [target.field.key]: trimmed })
    }
    put(contact.name, typedName)
    put(address, typed)
    return [...byStep.entries()].map(([stepId, values]) => ({ type: 'profile', stepId, values }))
  }

  /**
   * The claim, when it differs from what the session already holds.
   *
   * Reconciled against NOTHING, here as in the domain: no appointment is
   * looked up, no warning is raised when there is no booking on that day, and
   * nothing is linked. A stylist with the booking system open does that, and
   * does it far better than a matcher would. Resist adding one here.
   */
  const claimChange = ((): SessionCommand | null => {
    const wanted = claiming && claimDay.trim() !== '' ? claimDay.trim() : null
    if ((claim?.claimedDate ?? null) === wanted) return null
    return { type: 'claim_booking', claimedDate: wanted }
  })()

  /**
   * A guest is asked before she is sent, and only where there is somewhere to
   * put the answers. A flow that collects no contact details at all cannot be
   * made to hold a name, and refusing the send over a field that does not
   * exist would trap her on this screen forever — so the button submits, as it
   * does for a session that came from a booking.
   */
  const asksFirst = guest && (contact.name !== null || address !== null)
  const nameMissing = contact.name !== null && typedName.trim() === ''
  const addressMissing = address !== null && typed.trim() === ''
  /*
   * She cannot send without a name and an address.
   *
   * The screen after this one promises that a person will get in touch. A send
   * that leaves us no way to keep that promise is worse than a required field:
   * her list arrives, nobody can answer it, and she is waiting for a call that
   * cannot be made.
   */
  const canSend =
    !nameMissing && !addressMissing && nameRefusal === null && refusal === null && (!claiming || (claimDay.trim() !== '' && dayRefusal === null))

  /*
   * One batch: her details, her claim, then the send.
   *
   * The server applies a batch in order and persists it once, so this is
   * either a submitted session the boutique can reply to or it is nothing at
   * all. Submit goes last — everything before it has to land while the session
   * is still open to it.
   */
  const sendAsGuest = () => {
    ctx.send([...profileWrites(), ...(claimChange ? [claimChange] : []), { type: 'submit' }])
  }

  const share = async () => {
    const url = window.location.href
    const title = view.tenant.branding.displayName
    try {
      if (navigator.share) {
        await navigator.share({ title, url })
        return
      }
      await navigator.clipboard.writeText(url)
      setShared(true)
    } catch {
      setShared(false)
    }
  }

  const rail = (
    <div className="space-y-9">
      {view.flow.slots.map((slot) => {
        const state = view.slotStates.find((s) => s.key === slot.key)
        const picks = view.session.picks.filter((p) => p.slotKey === slot.key).sort((a, b) => a.position - b.position)
        if (picks.length === 0) return null
        const accent = slot.marker === 'primary' ? 'var(--fr-primary)' : 'var(--fr-secondary)'
        return (
          <section key={slot.key}>
            <div className="mb-3 flex items-baseline justify-between border-b pb-2" style={{ borderColor: 'var(--fr-rule)' }}>
              <h2 className="fr-eyebrow" style={{ color: accent }}>
                {text(slot.label, locale)}
              </h2>
              <span className="fr-eyebrow tabular-nums">
                {picks.length}
                {state ? `/${state.capacity}` : ''}
              </span>
            </div>

            <ul className="grid grid-cols-2 gap-3">
              {picks.map((pick) => {
                const card = byId.get(pick.garmentId)
                const isEditing = editing === pick.garmentId
                return (
                  <li key={pick.garmentId}>
                    {card ? (
                      <GarmentCardFace card={card} attributes={view.tenant.attributes} locale={locale} compact />
                    ) : (
                      <div className="fr-card-face flex h-32 items-center justify-center p-4 text-center">
                        {/* Never the id — this is the list she sends to the shop. */}
                        <span className="fr-eyebrow">{t.gownUnavailable}</span>
                      </div>
                    )}

                    {/*
                      Once she has sent it the rail is hers to READ, not to
                      edit: the domain refuses a `note` on a submitted session,
                      so an editor here would be a control that cannot save.
                      What she wrote still shows — it is part of the list she
                      came back to look at.
                    */}
                    {step.showNotes && submitted ? (
                      pick.note ? (
                        <p className="mt-2 text-[0.8125rem] leading-snug" style={{ color: 'var(--fr-ink-dim)' }}>
                          {pick.note}
                        </p>
                      ) : null
                    ) : step.showNotes ? (
                      isEditing ? (
                        <div className="mt-2">
                          <label className="fr-eyebrow block pb-1" htmlFor={`note-${pick.garmentId}`}>
                            {t.noteLabel}
                          </label>
                          <textarea
                            id={`note-${pick.garmentId}`}
                            className="w-full border bg-transparent p-2 text-[0.8125rem]"
                            style={{ borderColor: 'var(--fr-rule)', color: 'var(--fr-ink)', borderRadius: 'var(--fr-radius)' }}
                            rows={3}
                            maxLength={500}
                            value={draft}
                            onChange={(event) => setDraft(event.target.value)}
                          />
                          <button
                            type="button"
                            className="fr-btn-quiet"
                            disabled={ctx.pending}
                            onClick={() => {
                              ctx.send([{ type: 'note', garmentId: pick.garmentId, note: draft.trim() ? draft.trim() : null }])
                              setEditing(null)
                            }}
                          >
                            {t.saveNote}
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="fr-btn-quiet mt-1 block text-left"
                          onClick={() => {
                            setEditing(pick.garmentId)
                            // Her unsent note first, the saved one second —
                            // the same precedence the intake screen opens on.
                            setDraft(drafts.read(NOTE_SCOPE, pick.garmentId) ?? pick.note ?? '')
                          }}
                        >
                          {pick.note ? pick.note : t.addNote}
                        </button>
                      )
                    ) : null}
                  </li>
                )
              })}
            </ul>
          </section>
        )
      })}
    </div>
  )

  /*
   * "I have already booked", and the day.
   *
   * The checkbox reveals the date rather than the date standing alone: an
   * empty date field on a screen about an appointment reads as a question she
   * has to answer, and most guests have nothing to put in it.
   *
   * Rendered before the send on the guest's last step, and again on the panel
   * afterwards — she may only think of it once the screen tells her someone
   * will be in touch. Both are the same two controls over the same state, and
   * only ever one of them is on screen, so the ids cannot collide.
   */
  const claimFields = (
    <>
      <label className="flex items-start gap-3 py-1 text-[0.9375rem] leading-snug">
        <input
          type="checkbox"
          className="mt-0.5 h-5 w-5 shrink-0"
          style={{ accentColor: 'var(--fr-primary)' }}
          checked={claiming}
          onChange={(event) => setClaiming(event.target.checked)}
        />
        <span>{t.claimToggle}</span>
      </label>
      {claiming ? (
        <div className="mt-3 flex flex-col gap-1.5">
          <label className="fr-eyebrow" htmlFor="fr-claim-date">
            {t.claimDateLabel}
          </label>
          <input
            id="fr-claim-date"
            className="fr-input"
            type="date"
            aria-invalid={dayRefusal ? true : undefined}
            value={claimDay}
            onChange={(event) => setClaimDay(event.target.value)}
          />
          {dayRefusal ? (
            <p className="text-[0.8125rem] leading-snug" role="alert" style={{ color: 'var(--fr-secondary)' }}>
              {dayRefusal}
            </p>
          ) : (
            <p className="text-[0.8125rem] leading-snug" style={{ color: 'var(--fr-ink-faint)' }}>
              {t.claimHint}
            </p>
          )}
        </div>
      ) : null}
    </>
  )

  /*
   * The least that makes the next screen true, asked in place of the rail.
   *
   * In place of, rather than above it: the send control is sticky at the
   * bottom, so a form appended to a rail she has scrolled halfway down would
   * open somewhere she cannot see. Swapping the body makes the screen change
   * where she is looking, and the way back to the list is under the fields.
   */
  const guestForm = (
    <div className="space-y-5">
      {contact.name ? (
        <div className="flex flex-col gap-1.5">
          <label className="fr-eyebrow" htmlFor="fr-guest-name">
            {t.nameLabel}
          </label>
          <input
            id="fr-guest-name"
            className="fr-input"
            type="text"
            autoComplete="name"
            maxLength={contact.name.field.maxLength}
            aria-invalid={nameRefusal ? true : undefined}
            value={typedName}
            onChange={(event) => setTypedName(event.target.value)}
          />
          {nameRefusal ? (
            <p className="text-[0.8125rem] leading-snug" role="alert" style={{ color: 'var(--fr-secondary)' }}>
              {nameRefusal}
            </p>
          ) : null}
        </div>
      ) : null}

      {address ? (
        <div className="flex flex-col gap-1.5">
          <label className="fr-eyebrow" htmlFor="fr-guest-email">
            {t.emailLabel}
          </label>
          <input
            id="fr-guest-email"
            className="fr-input"
            type="email"
            autoComplete="email"
            maxLength={address.field.maxLength}
            aria-invalid={refusal ? true : undefined}
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
          />
          {refusal ? (
            <p className="text-[0.8125rem] leading-snug" role="alert" style={{ color: 'var(--fr-secondary)' }}>
              {refusal}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="border p-4" style={{ borderColor: 'var(--fr-rule)', borderRadius: 'var(--fr-radius)' }}>
        {claimFields}
      </div>

      <div className="pt-1 text-center">
        <button type="button" className="fr-btn-quiet fr-btn-target" onClick={() => setAsking(false)}>
          {t.backToList}
        </button>
      </div>
    </div>
  )

  /*
   * What the rail collapses into.
   *
   * The screen has to CHANGE when she presses send — leaving the rail exactly
   * as it was, with the button swapped for a line of small caps, reads as
   * though nothing happened. It hangs on `session.status` rather than on a
   * flag set by the button, so the link she opens again tomorrow lands here
   * too. And it is a collapse rather than a deletion: she may well have opened
   * the link precisely to look at her list.
   */
  const panel = (
    <div className="space-y-3">
      {/*
        A guest can still say she has booked, or take it back, after sending —
        the screen above is where she learns that saying so is even an option.
        The domain allows exactly this and nothing else beside `profile`,
        because both change how the boutique REACHES her, never what it pulls.
        There is no "saved" line: the body of this panel is already the answer,
        turning from "someone will be in touch" into the date she gave.
      */}
      {guest ? (
        <div className="border p-4" style={{ borderColor: 'var(--fr-rule)', borderRadius: 'var(--fr-radius)' }}>
          {claimFields}
          <button
            type="button"
            className="fr-btn fr-btn-outline mt-3 w-full"
            disabled={ctx.pending || dayRefusal !== null || claimChange === null}
            onClick={() => {
              if (claimChange) ctx.send([claimChange])
            }}
          >
            {t.saveBooking}
          </button>
        </div>
      ) : null}

      {address ? (
        panelOpen ? (
          <div className="border p-4" style={{ borderColor: 'var(--fr-rule)', borderRadius: 'var(--fr-radius)' }}>
            <label className="fr-eyebrow block pb-1.5" htmlFor="fr-send-to-me">
              {t.emailLabel}
            </label>
            <input
              id="fr-send-to-me"
              className="fr-input"
              type="email"
              autoComplete="email"
              maxLength={address.field.maxLength}
              aria-invalid={refusal ? true : undefined}
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
            />
            {refusal ? (
              <p className="mt-1.5 text-[0.8125rem] leading-snug" role="alert" style={{ color: 'var(--fr-secondary)' }}>
                {refusal}
              </p>
            ) : (
              <p className="mt-1.5 text-[0.8125rem] leading-snug" style={{ color: 'var(--fr-ink-faint)' }}>
                {t.emailPromise}
              </p>
            )}
            <button
              type="button"
              className="fr-btn fr-btn-outline mt-3 w-full"
              disabled={ctx.pending || refusal !== null || typed.trim() === ''}
              onClick={keepAddress}
            >
              {t.saveEmail}
            </button>
            {/* Present before it has anything to say, so it is a region that can announce. */}
            <p className="mt-2 text-[0.8125rem] leading-snug" role="status" style={{ color: 'var(--fr-primary)' }}>
              {saved && stored !== undefined ? t.emailSaved(String(stored)) : ''}
            </p>
          </div>
        ) : (
          <button type="button" className="fr-btn fr-btn-outline w-full" onClick={() => setPanelOpen(true)}>
            {t.emailToMe}
          </button>
        )
      ) : null}

      {step.shareable ? (
        <button type="button" className="fr-btn fr-btn-outline w-full" onClick={() => void share()}>
          {shared ? t.shareCopied : t.share}
        </button>
      ) : null}

      {/*
        The third control: start the whole thing again.

        What she sent stays exactly where it is — the boutique goes on working
        from it until the new one is finished — so the line underneath says so
        before she taps. Tapping twice lands on the same new session: the
        server returns the refill she already started rather than making
        another, so a double press cannot leave two half-filled lists behind.
      */}
      {!deadlinePassed ? (
        <div>
          <button
            type="button"
            className="fr-btn fr-btn-outline w-full"
            disabled={refill.isPending}
            onClick={() =>
              refill.mutate(undefined, {
                onSuccess: (started) => {
                  // The new session's own link. `url` is absolute and correct
                  // for a text message; inside the app the path is what keeps
                  // her on the origin she is already on.
                  navigate(`/s/${started.token}`)
                  window.scrollTo({ top: 0 })
                },
              })
            }
          >
            {t.fillAgain}
          </button>
          <p className="mt-1.5 text-[0.8125rem] leading-snug" style={{ color: 'var(--fr-ink-faint)' }}>
            {t.fillAgainBody}
          </p>
          {/* Said where she is looking when she presses it, as every other refusal on this screen is. */}
          {refill.isError ? (
            <p className="mt-1 text-[0.8125rem] leading-snug" role="alert" style={{ color: 'var(--fr-secondary)' }}>
              {t.fillAgainFailed}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="pt-1 text-center">
        <button
          type="button"
          className="fr-btn-quiet fr-btn-target"
          // The rail renders directly under this control when it opens, so
          // `aria-expanded` alone says what happened — an `aria-controls`
          // pointing at an id that is not in the document while collapsed
          // says less than nothing.
          aria-expanded={railOpen}
          onClick={() => setRailOpen((open) => !open)}
        >
          {railOpen ? t.hideList : t.showList}
        </button>
      </div>
    </div>
  )

  /**
   * Which of the two things is about to happen, in her own words back to her.
   *
   * A session with an appointment behind it says what it always said. A guest
   * is told either that someone will be in touch, or — because she said so —
   * that we will go and find the booking she named.
   */
  const sentBody = !guest
    ? t.sentBody
    : claim
      ? t.sentClaimBody(formatDay(claim.claimedDate, locale))
      : t.sentGuestBody

  return (
    <StepShell
      wide
      title={submitted ? t.sentTitle : asking ? t.guestContactTitle : text(step.title, locale) || t.yourRail}
      body={submitted ? sentBody : asking ? t.guestContactBody : text(step.body, locale)}
      footer={
        submitted ? undefined : (
          <div className="pb-2" style={{ background: 'linear-gradient(to top, var(--fr-ground-deep) 62%, transparent)' }}>
            <button
              type="button"
              className="fr-btn fr-btn-primary w-full"
              disabled={ctx.pending || (asking && !canSend)}
              onClick={() => {
                if (asksFirst && !asking) setAsking(true)
                else if (asking) sendAsGuest()
                else ctx.send([{ type: 'submit' }])
              }}
            >
              {t.submit}
            </button>
            {/* Why it is refusing, where she is looking when she presses it. */}
            {asking && (nameMissing || addressMissing) ? (
              <p className="pt-2 text-center text-[0.8125rem] leading-snug" style={{ color: 'var(--fr-ink-faint)' }}>
                {t.contactNeeded}
              </p>
            ) : null}
          </div>
        )
      }
    >
      {submitted ? (
        <div className="space-y-8">
          {panel}
          {railOpen ? rail : null}
        </div>
      ) : asking ? (
        guestForm
      ) : (
        <>
          {/* A deadline she can still act on. Past the send it would be a promise about a rail that no longer moves. */}
          {view.session.editableUntil ? (
            <p className="fr-eyebrow mb-6">{t.editableUntil(formatDateTime(view.session.editableUntil, locale))}</p>
          ) : null}
          {rail}
        </>
      )}
    </StepShell>
  )
}
